import React, { useState, ReactNode, FocusEvent, ChangeEvent, useEffect } from 'react'
import { TextInputIconType } from 'components/atoms/TextInput'
import get from 'lodash/get'
import orderBy from 'lodash/orderBy'
import { AutocompleteChangeReason, AutocompleteChangeDetails } from 'transfix-ui/core'
import { Autocomplete, IAutocompleteOption, autocompleteChangeReasons } from '../Autocomplete'
import * as Recipes from './utils/recipes'
import { getOptionGroupDetails, EMPTY_AUTOCOMPLETE_VALUE } from './utils/utils'

/**
 * Used for wrapper components such as <TrailerAutosuggest />
 * Keeps types in strict uniformity across components
 */
export interface IAutosuggestWrapperProps<T, M = T> {
  id?: string
  /**
   * T is set here
   */
  value: Partial<T> | undefined
  /**
   * Refresh the options source on mount
   */
  fetchList?: boolean
  /**
   * Error from validations
   */
  error?: string
  /**
   * Placeholder text
   */
  placeholder?: string
  /**
   * If the field is required
   */
  required?: boolean
  /**
   * Send a partial update to parent
   */
  onChange: (val: Partial<M> | undefined) => void
  /**
   * On blur action
   */
  onBlur?: (e: FocusEvent<HTMLInputElement>) => void
  /**
   * Toggle the parent Add Entity form state
   * @param {string} [inputVal] - can also be undefined
   */
  onAddEntity?: (inputVal?: string) => void
  /**
   * Cancels form
   */
  onCancel?: () => void
  disabled?: boolean
  dataTest?: string
  label?: string
  large?: boolean
  onFocus?: (e: FocusEvent<HTMLInputElement>) => void
  onInputChange?: (val?: string) => void
  icon?: keyof typeof TextInputIconType
  /**
   * Highlights the field with a background color.
   * @default false
   */
  highlighted?: boolean
  clearOnBlur?: boolean
  /**
   * Filters results based on motor carrier id
   * @default false
   */
  selectedMotorCarrierId?: number
}

export interface IAutoSuggestProps {
  options?: IAutocompleteOption[]
  defaultValue?: IAutocompleteOption | {}
  value?: IAutocompleteOption | {}
  error?: string
  label?: string
  large?: boolean
  placeholder?: string
  dataTest?: string
  id?: string
  required?: boolean
  clearable?: boolean
  icon?: keyof typeof TextInputIconType
  /**
   * Render a custom option: 'default | 'withAvatar'
   */
  renderOptionType?: Recipes.RenderOptionType
  /**
   * Render a custom `startAdornment`: 'default | 'withAvatar'
   */
  startAdornmentType?: Recipes.StartAdornmentType
  /**
   * Render a custom group: 'default'
   */
  renderGroupType?: Recipes.RenderGroupType
  /**
   * Render
   */
  filterOptionsType?: Recipes.FilterOptionType
  /**
   * Text to display when there are no options.
   */
  noOptionsText?: ReactNode
  /**
   * Used to determine the string value for a given option.
   * Fills the input (and the list box options if `renderOption` is not provided)
   * @param {IAutocompleteOption} option
   * @returns {string}
   */
  getOptionLabel?: (option: IAutocompleteOption) => string
  /**
   * Calllback fired to cancel form
   */
  onCancel?: () => void
  /**
   * Callback fired when the input value changes.
   * @param {string} [inputVal] - can also be undefined
   */
  onAddEntity?: (inputVal?: string) => void
  /**
   * Callback fired when the input value changes.
   * @param {string} value
   */
  onInputChange?: (value: string) => void
  /**
   * Callback fired when the value changes.
   * @param {IAutocompleteOption} [val] The new value of the component.
   */
  onChange?: (val?: IAutocompleteOption) => void
  onFocus?: (e: FocusEvent<HTMLInputElement>) => void
  onBlur?: (e: FocusEvent<HTMLInputElement>) => void
  onClearButtonClick?: Function
  autoFocus?: boolean
  open?: boolean
  /**
   * If `true`, the input's text will be cleared on blur if no value is selected.
   *
   * Set to `true` if you want to help the user enter a new value.
   * Set to `false` if you want to help the user resume his search.
   * @default false
   */
  clearOnBlur?: boolean
  /**
   * If `true`, clear all values when the user presses escape and the popup is closed.
   * @default false
   */
  clearOnEscape?: boolean
  /**
   * If `true`, the selected option becomes the value of the input
   * when the Autocomplete loses focus unless the user chooses
   * a different option or changes the character string in the input.
   * @default false
   */
  autoSelect?: boolean
  /**
   * If `true`, the first option is automatically highlighted.
   * @default false
   */
  /**
   * Limit # of displayed options when a search query is entered
   * -1 indicates no limit
   * @default 6
   */
  limitOptions?: number
  /**
   * If true, the input's text will be selected on focus.
   * It helps the user clear the selected value.
   * @default true
   */
  selectOnFocus?: boolean
  autoHighlight?: boolean
  disabled?: boolean
  isLoading?: boolean
  /**
   * Enables defining a custom function to parse `inputProps.value`
   * and select a substring to display in `<TextInput />`
   * @default undefined
   */
  parseInputValue?: (val: string) => string
  /**
   * Highlights the field with a background color.
   * @default false
   */
  highlighted?: boolean
  /**
   * If `true`, the Autocomplete is free solo, meaning that the user input is not bound to provided options.
   */
  freeSolo?: boolean
}

/** @deprecated Create an abstraction of <AutocompleteBase /> instead */
export function Autosuggest({
  dataTest,
  id: idProp,
  icon: iconProp,
  clearable: clearableProp,
  options: optionsProp = [],
  value: valueProp,
  defaultValue: defaultValueProp,
  onAddEntity: onAddEntityProp,
  onChange: onChangeProp,
  onCancel: onCancelProp,
  error: errorProp,
  label: labelProp,
  large: largeProp = true,
  getOptionLabel: getOptionLabelProp = (o?: IAutocompleteOption) => (o as any)?.text || '',
  placeholder: placeholderProp,
  onInputChange: onInputChangeProp,
  renderOptionType: renderOptionTypeProp = 'default',
  startAdornmentType: startAdornmentTypeProp = 'default',
  renderGroupType: renderGroupTypeProp = 'default',
  filterOptionsType: filterOptionsTypeProp = 'filter',
  onFocus: onFocusProp,
  onBlur: onBlurProp,
  onClearButtonClick: onClearButtonClickProp,
  required: requiredProp,
  open: openProp,
  clearOnBlur: clearOnBlurProp = false,
  clearOnEscape: clearOnEscapeProp = false,
  autoSelect: autoSelectProp = false,
  autoHighlight: autoHighlightProp = false,
  disabled: disabledProp,
  selectOnFocus: selectOnFocusProp = true,
  parseInputValue: parseInputValueProp,
  isLoading,
  limitOptions = 6,
  highlighted = false,
  freeSolo,
}: IAutoSuggestProps) {
  // Key is used to reset instance of <Autocomplete />
  const [autocompleteKey, setAutocompleteKey] = useState(Date.now())

  const optionsWithDefaultValues = [...optionsProp, EMPTY_AUTOCOMPLETE_VALUE]

  /**
   * Generate an object whose keys are `groupIndex` so each value
   * is easilly accessible
   */
  const optionGroupsByGroupIndex = getOptionGroupDetails(optionsWithDefaultValues)

  /**
   * Returns list of `options` grouped by `groupIndex` in ascending order
   */
  const getOptions = () => {
    return orderBy(optionsWithDefaultValues, 'groupIndex')
  }
  /**
   * Provides more control around setting the default value
   */
  const getValue = (
    opts: IAutocompleteOption[],
    val: IAutocompleteOption | {} | undefined
  ): IAutocompleteOption => {
    if (!val || !(val as IAutocompleteOption)?.id) return EMPTY_AUTOCOMPLETE_VALUE
    return opts.find(({ id }) => `${id}` === `${get(val, 'id')}`) || EMPTY_AUTOCOMPLETE_VALUE
  }

  /**
   * Render group with conditional class if it contains `unassigned` or `addOption`.
   * Groups are ordered by `groupIndex` value, which is an integer
   */
  const renderGroup = Recipes.getRenderGroupProps(renderGroupTypeProp, optionGroupsByGroupIndex)

  /**
   * Clear the form in the parent element by returning `undefined` with `onChange`
   */
  const clearForm = (reset?: boolean) => {
    // Reset key, and initialize form
    if (reset) setAutocompleteKey(Date.now())
    if (onChangeProp) onChangeProp({} as IAutocompleteOption)
  }

  /**
   * Handles selecting `unassigned`, `add new driver` or a valid option
   */
  const selectOption = (details: AutocompleteChangeDetails<IAutocompleteOption> | undefined) => {
    const { option: selectedOption } = details || {}
    const { text = '' } = selectedOption || {}

    // Parses the 'Add "name here"' statement and only uses text within quotes
    if (text?.toLowerCase().match(/add\s/) && onAddEntityProp) {
      const [, match = ''] = text.match(/"(.*?)"/) || [] // gets matched text inside quotes
      onAddEntityProp(match)
    } else if (selectedOption && onChangeProp && selectedOption?.id) {
      const selected = optionsWithDefaultValues.find(({ id }) => id === selectedOption.id)
      onChangeProp(selected)
    } else if (selectedOption && onChangeProp) {
      onChangeProp({} as IAutocompleteOption)
    }
  }

  const optionSelectHandler = (
    event: ChangeEvent<{}>,
    val: any,
    reason: AutocompleteChangeReason,
    details: AutocompleteChangeDetails<IAutocompleteOption> | undefined
  ) => {
    const { blur, clear, selectOption: select } = autocompleteChangeReasons || {}

    // Since `event` is typed with an object, we have to work
    // around type. But we can be sure it's from an input with
    // an `target.id` prop in this instance
    if (reason === clear && !!(event?.target as any)?.id) {
      clearForm(false)
      if (onCancelProp) onCancelProp()
    } else if (reason === blur) {
      clearForm(false)
    } else if (reason === select) {
      selectOption(details)
    }
  }

  const groupBy = (option: IAutocompleteOption) => `${option.groupIndex}`

  const inputChangeHandler = (event: ChangeEvent<any>, v: string = '') => {
    /**
     * Makes sure not to trigger the event handler if the event type is
     * 'click', meaning an option was selected from the dropdown. This will be
     * handled by `onChangeProp`.
     */
    if (!!event && onInputChangeProp && event.type !== 'click') onInputChangeProp(v)
  }

  // These all have default values
  const startAdornment = Recipes.getStartAdornmentProps(startAdornmentTypeProp)
  const renderOption = Recipes.getRenderOptionProps(renderOptionTypeProp, getOptionLabelProp)
  const filterOptions = Recipes.getFilterOptionProps(filterOptionsTypeProp)

  const options = getOptions()
  const icon = iconProp ? TextInputIconType[iconProp] : undefined

  const getOptionSelected = (opt: IAutocompleteOption, val: IAutocompleteOption): boolean => {
    return opt?.id === val?.id
  }

  // Get initial value and store in state
  const [value, setValue] = useState<IAutocompleteOption>(getValue(options, valueProp))
  const [defaultValue] = useState<IAutocompleteOption>(getValue(options, defaultValueProp))

  // Update if a new/different initial prop is passed in
  // Or if the options changed
  useEffect(() => {
    const valuePropId = `${get(valueProp, 'id', '')}`
    const valueId = `${get(value, 'id', '')}`

    if (valueId !== valuePropId) {
      setValue(getValue(options, valueProp))
    }
  }, [valueProp, options, value])

  /**
   * If `disabled` is true, clearable should be false. Otherwise use default
   */
  const isClearable = disabledProp ? false : clearableProp

  return (
    <Autocomplete
      clearable={isClearable}
      id={idProp}
      dataTest={dataTest}
      required={requiredProp}
      icon={icon}
      key={autocompleteKey}
      placeholder={placeholderProp}
      large={largeProp}
      label={labelProp}
      value={value}
      defaultValue={defaultValue}
      startAdornment={startAdornment}
      renderOption={renderOption}
      getOptionLabel={getOptionLabelProp}
      onClearButtonClick={onClearButtonClickProp}
      onChange={optionSelectHandler}
      onFocus={onFocusProp}
      onBlur={onBlurProp}
      options={options}
      onInputChange={inputChangeHandler}
      renderGroup={renderGroup}
      error={errorProp}
      groupBy={groupBy}
      filterOptions={filterOptions}
      open={openProp}
      clearOnBlur={clearOnBlurProp}
      clearOnEscape={clearOnEscapeProp}
      autoSelect={autoSelectProp}
      autoHighlight={autoHighlightProp}
      isOptionEqualToValue={getOptionSelected}
      disabled={disabledProp}
      isLoading={isLoading}
      limitOptions={limitOptions}
      selectOnFocus={selectOnFocusProp}
      parseInputValue={parseInputValueProp}
      highlighted={highlighted}
      freeSolo={freeSolo}
    />
  )
}

export default Autosuggest
